import { OPTICLOCK_MINUTE_STEP } from '@staff-app/shared-types';

/**
 * Duration formatting for OptiClock.
 *
 * `formatHoursMinutes` is a VENDORED COPY of apps/api/src/app/opticlock/opticlock.format.ts —
 * apps/api does not depend on @staff-app/shared-types, so the function cannot be shared. Both
 * copies carry the SAME test table, and that shared table is the drift detector. If you change
 * one, change the other.
 */

/** Minutes → `H:MM`. Hours are not zero-padded and not wrapped at 24 (`6045 → '100:45'`). */
export function formatHoursMinutes(minutes: number): string {
  const total = Number.isFinite(minutes) ? Math.max(0, Math.trunc(minutes)) : 0;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}

/**
 * The values the minutes picker offers: 0:15 … 8:00 in 15-minute steps.
 *
 * A closed option list rather than a number input, so the control CANNOT express a value the
 * CHECK constraint would reject. 8:00 is the top of the list, not the top of what is allowed —
 * a longer day is entered as two rows, which is also how it reads on the timesheet.
 */
export const DURATION_OPTIONS: Array<{ value: number; label: string }> = Array.from(
  { length: (8 * 60) / OPTICLOCK_MINUTE_STEP },
  (_, i) => {
    const minutes = (i + 1) * OPTICLOCK_MINUTE_STEP;
    return { value: minutes, label: formatHoursMinutes(minutes) };
  },
);

/**
 * A share of a total, as a percentage string: `73.33%`, `50%`, `0%`.
 *
 * Two decimals, with a trailing `.00` trimmed — the reference tool shows `73.33% / 26.67%`, and
 * rounding those to whole numbers loses the distinction between a 2/3 : 1/3 split and a 70/30
 * one, which is the comparison the tooltip exists to support. An even split still reads as a
 * clean `50%` rather than a noisy `50.00%`.
 */
export function formatPercent(part: number, total: number): string {
  if (!Number.isFinite(part) || !Number.isFinite(total) || total <= 0) return '0%';
  const pct = (part / total) * 100;
  return `${pct.toFixed(2).replace(/\.00$/, '')}%`;
}
